import {
  hasLineItemChildren,
  isCompositeFieldType,
} from '../components/fieldRenderers';
import { ILabel } from '../constants/interfaces';
import { IFormStateValue } from './formUtils';
import { isFieldVisible, getParentLabelId } from './visibilityUtils';

function sortLabelsByOrderIndex(a: ILabel, b: ILabel): number {
  return Number(a.orderIndex) - Number(b.orderIndex);
}

/** Line items under summation / child_row_list can own extra labels (custom questions) via parent_label_id. */
function attachLineItemNestedChildren(
  lineItems: ILabel[],
  allFields: ILabel[]
): ILabel[] {
  return lineItems
    .slice()
    .sort(sortLabelsByOrderIndex)
    .map((lineItem) => ({
      ...lineItem,
      children: allFields
        .filter((f) => getParentLabelId(f) === lineItem.labelId)
        .sort(sortLabelsByOrderIndex),
    }));
}

function mapVisibleFields(
  fields: ILabel[],
  formState: Record<string, IFormStateValue>
) {
  // Composite parents own child labels. Which types those are, and whether their
  // children are line items with nested labels of their own, comes from the field
  // renderer registry rather than being listed by name here.
  const compositeParents = fields.filter((f) => isCompositeFieldType(f.type));
  const lineItemParents = compositeParents.filter((f) =>
    hasLineItemChildren(f.type)
  );
  const childMap: Record<string, ILabel[]> = {};

  compositeParents.forEach((parent) => {
    const children = fields.filter(
      (field) => getParentLabelId(field) === parent.labelId
    );
    childMap[parent.labelId] = hasLineItemChildren(parent.type)
      ? attachLineItemNestedChildren(children, fields)
      : children;
  });

  const lineItemLabelIds = new Set<number>();
  lineItemParents.forEach((composite) => {
    fields.forEach((f) => {
      if (getParentLabelId(f) === composite.labelId) {
        lineItemLabelIds.add(f.labelId);
      }
    });
  });

  // Remove composite direct children and labels whose parent is a line item (nested under row UI)
  let visibleFields = fields.filter((field) => {
    const pid = getParentLabelId(field);
    if (compositeParents.some((parentField) => pid === parentField.labelId)) {
      return false;
    }
    if (pid !== undefined && lineItemLabelIds.has(pid)) {
      return false;
    }
    return true;
  });

  // Attach children array to each composite parent field
  visibleFields = visibleFields.map((field) => {
    if (isCompositeFieldType(field.type) && childMap[field.labelId]) {
      return {
        ...field,
        children: childMap[field.labelId],
      };
    }
    return field;
  });

  // Apply visibility logic using only the new JSON-based visibility rules system
  return visibleFields
    .filter((field) => {
      // Use the new visibility logic that supports JSON-based rules
      // All visibility is now controlled by visibilityRules
      return isFieldVisible(field, formState);
    })
    .sort((a, b) => {
      const aOrder = Number(a.orderIndex);
      const bOrder = Number(b.orderIndex);
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      // If orderIndex is the same, sort alphabetically by label
      const aLabel = (a.text || '').toString().toLowerCase();
      const bLabel = (b.text || '').toString().toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
}

export default function getVisibleFields(
  fields: ILabel[],
  formState: Record<string, IFormStateValue>
) {
  return mapVisibleFields(fields, formState);
}
